import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "layer-manager-panel" }
const _hoisted_2 = { class: "panel-header" }
const _hoisted_3 = { class: "search-box" }
const _hoisted_4 = {
  key: 0,
  class: "active-summary"
}
const _hoisted_5 = { class: "summary-header" }
const _hoisted_6 = { class: "date-control" }
const _hoisted_7 = { class: "date-label" }
const _hoisted_8 = { class: "summary-list" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "summary-name" }
const _hoisted_11 = ["onClick"]
const _hoisted_12 = {
  key: 1,
  class: "layer-list"
}
const _hoisted_13 = ["id", "onClick"]
const _hoisted_14 = { class: "item-header" }
const _hoisted_15 = { class: "checkbox" }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = { class: "layer-info" }
const _hoisted_18 = { class: "layer-name" }
const _hoisted_19 = { class: "layer-id" }
const _hoisted_20 = { class: "slider-row" }
const _hoisted_21 = ["value", "onInput"]
const _hoisted_22 = {
  key: 2,
  class: "empty-state"
}
const _hoisted_23 = { key: 0 }
const _hoisted_24 = { key: 1 }

import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import MaterialCalendar from "../ui/MaterialCalendar.vue";

interface WMSLayer {
	Name: string;
	Title: string;
	Abstract?: string;
}

interface ActiveLayer {
	id: string;
	title: string;
	opacity: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LayerManager',
  props: {
    layers: {},
    activeLayers: {},
    loading: { type: Boolean },
    wmsDate: {},
    availableDatesMap: {}
  },
  emits: [
	"update:activeLayers",
	"close",
	"update:wmsDate",
	"check-availability",
],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const searchQuery = ref("");
const localActiveLayers = ref<ActiveLayer[]>([]);
const showCalendar = ref(false);

// Formatting date for display
const formattedDate = computed(() => {
	if (!props.wmsDate) return "Oggi";
	const d = new Date(props.wmsDate);
	// Check if valid
	if (isNaN(d.getTime())) return props.wmsDate;
	return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
});

// Close calendar on click outside
function handleClickOutside(e: MouseEvent) {
	const target = e.target as HTMLElement;
	if (showCalendar.value && !target.closest(".date-control")) {
		showCalendar.value = false;
	}
}

onMounted(() => {
	document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
	document.removeEventListener("click", handleClickOutside);
});

// Sync prop to local
watch(
	() => props.activeLayers,
	(newVal) => {
		localActiveLayers.value = [...newVal];
	},
	{ immediate: true, deep: true },
);

const filteredLayers = computed(() => {
	if (!searchQuery.value) return props.layers;
	const q = searchQuery.value.toLowerCase();
	return props.layers.filter(
		(l) =>
			l.Title.toLowerCase().includes(q) || l.Name.toLowerCase().includes(q),
	);
});

// [NEW] Merge dates from all active layers
const mergedAvailableDates = computed(() => {
	if (localActiveLayers.value.length === 0) return [];

	// If we have dates for specific layers, merge them.
	// Ideally user selects ONE date for all layers, so we show INTERSECTION or UNION.
	// Union is safer (shows where *something* is available).

	const uniqueDates = new Set<string>();

	localActiveLayers.value.forEach((layer) => {
		const dates = props.availableDatesMap?.[layer.id] || [];
		dates.forEach((d) => uniqueDates.add(d));
	});

	// Also include currently selected date if valid
	if (props.wmsDate) uniqueDates.add(props.wmsDate);

	return Array.from(uniqueDates);
});

function onCalendarMonthChange(event: { year: number; month: number }) {
	// Fetch availability for all active layers for this month
	localActiveLayers.value.forEach((layer) => {
		emit("check-availability", {
			year: event.year,
			month: event.month,
			layerId: layer.id,
		});
	});
}

function isSelected(name: string) {
	return localActiveLayers.value.some((l) => l.id === name);
}

function getActiveOpacity(name: string) {
	const layer = localActiveLayers.value.find((l) => l.id === name);
	return layer ? layer.opacity : 1.0;
}

function toggleLayer(layer: WMSLayer) {
	const idx = localActiveLayers.value.findIndex((l) => l.id === layer.Name);
	if (idx >= 0) {
		// Remove
		localActiveLayers.value.splice(idx, 1);
	} else {
		// Add
		localActiveLayers.value.push({
			id: layer.Name,
			title: layer.Title,
			opacity: 1.0,
		});
	}
	updateActive();
}

function removeLayer(index: number) {
	localActiveLayers.value.splice(index, 1);
	updateActive();
}

function updateLayerOpacity(id: string, event: Event) {
	const val = parseFloat((event.target as HTMLInputElement).value);
	const layer = localActiveLayers.value.find((l) => l.id === id);
	if (layer) {
		layer.opacity = val;
		updateActive();
	}
}

function updateActive() {
	emit("update:activeLayers", localActiveLayers.value);
}

function scrollToLayer(layerId: string) {
	const el = document.getElementById(`layer-item-${layerId}`);
	if (el) {
		el.scrollIntoView({ behavior: "smooth", block: "center" });
	}
}

function onDateSelect(newDate: string) {
	emit("update:wmsDate", newDate);
	showCalendar.value = false;
}

// [NEW] Clear date
function clearDate() {
	emit("update:wmsDate", null);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[6] || (_cache[6] = _createElementVNode("div", { class: "header-title" }, [
        _createElementVNode("span", { class: "icon" }, "🛰️"),
        _createElementVNode("h3", null, "Livelli Satellite")
      ], -1)),
      _createElementVNode("button", {
        class: "close-btn",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
      }, "❌")
    ]),
    _createElementVNode("div", _hoisted_3, [
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
        type: "text",
        placeholder: "Cerca nuovo livello...",
        class: "search-input"
      }, null, 512), [
        [_vModelText, searchQuery.value]
      ])
    ]),
    (localActiveLayers.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _cache[8] || (_cache[8] = _createElementVNode("div", { class: "summary-title" }, "Livelli Attivi", -1)),
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("button", {
                class: "calendar-btn",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (showCalendar.value = !showCalendar.value)),
                title: "Seleziona data"
              }, [
                _cache[7] || (_cache[7] = _createElementVNode("span", { class: "calendar-icon" }, "📅", -1)),
                _createElementVNode("span", _hoisted_7, _toDisplayString(formattedDate.value), 1)
              ]),
              (props.wmsDate)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    class: "clear-date-btn",
                    onClick: clearDate,
                    title: "Torna a Oggi"
                  }, " ✕ "))
                : _createCommentVNode("", true),
              (showCalendar.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: "calendar-popover",
                    onClick: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["stop"]))
                  }, [
                    _createVNode(MaterialCalendar, {
                      modelValue: __props.wmsDate,
                      activityDates: mergedAvailableDates.value,
                      "onUpdate:modelValue": onDateSelect,
                      onClose: _cache[3] || (_cache[3] = ($event: any) => (showCalendar.value = false)),
                      onMonthChange: onCalendarMonthChange
                    }, null, 8, ["modelValue", "activityDates"])
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_8, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localActiveLayers.value, (layer, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: layer.id,
                class: "summary-item",
                onClick: ($event: any) => (scrollToLayer(layer.id)),
                style: {"cursor":"pointer"}
              }, [
                _createElementVNode("span", _hoisted_10, _toDisplayString(layer.title), 1),
                _createElementVNode("button", {
                  class: "remove-btn-small",
                  onClick: _withModifiers(($event: any) => (removeLayer(index)), ["stop"]),
                  title: "Rimuovi livello"
                }, " ✕ ", 8, _hoisted_11)
              ], 8, _hoisted_9))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    (filteredLayers.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredLayers.value, (layer) => {
            return (_openBlock(), _createElementBlock("div", {
              key: layer.Name,
              id: `layer-item-${layer.Name}`,
              class: _normalizeClass(["layer-item", { active: isSelected(layer.Name) }]),
              onClick: _withModifiers(($event: any) => (toggleLayer(layer)), ["stop"])
            }, [
              _createElementVNode("div", _hoisted_14, [
                _createElementVNode("div", _hoisted_15, [
                  (isSelected(layer.Name))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_16, "✓"))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_17, [
                  _createElementVNode("span", _hoisted_18, _toDisplayString(layer.Title), 1),
                  _createElementVNode("span", _hoisted_19, _toDisplayString(layer.Name), 1)
                ])
              ]),
              (isSelected(layer.Name))
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "inline-opacity",
                    onClick: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["stop"]))
                  }, [
                    _createElementVNode("div", _hoisted_20, [
                      _createElementVNode("label", null, "Opacità " + _toDisplayString(Math.round(getActiveOpacity(layer.Name) * 100)) + "%", 1)
                    ]),
                    _createElementVNode("input", {
                      type: "range",
                      min: "0",
                      max: "1",
                      step: "0.05",
                      value: getActiveOpacity(layer.Name),
                      onInput: (e) => updateLayerOpacity(layer.Name, e)
                    }, null, 40, _hoisted_21)
                  ]))
                : _createCommentVNode("", true)
            ], 10, _hoisted_13))
          }), 128))
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_22, [
          (__props.loading)
            ? (_openBlock(), _createElementBlock("span", _hoisted_23, "Caricamento livelli..."))
            : (_openBlock(), _createElementBlock("span", _hoisted_24, "Nessun livello trovato."))
        ]))
  ]))
}
}

})